import { html, nothing, type TemplateResult } from "lit";

// Yetenek listesinde geçen ve core'a ait olan elementlerin kaydı.
// Kayıt olmazsa etiket sessizce boş kalır — bu yüzden burada zorunlu tutuluyor.
import "../drag/FileDropElement";
import "../drag/ItemDropElement";

/**
 * Bir ürün viewport'unun içine konabilecek yetenek elementleri.
 *
 * Bugüne kadar her ürün bu etiketleri kendi şablonuna elle yazıyordu; yeni bir
 * post-process eklendiğinde üç şablon birden güncellenmek zorundaydı. Artık tek
 * liste burada.
 */
export type ViewportCapability =
  // kamera / girdi
  | "smooth-orbit"
  | "pointer-gesture-interpreter"
  | "sugar-file-drop"
  | "sugar-item-drop"
  // ürün içeriği
  | "product-viewer"
  | "designer-transformer"
  | "configurator-bridge"
  | "configurator-drag-preview"
  // pipeline
  | "ssgi-postprocess"
  | "gtao-accumulate-postprocess"
  | "outline-postprocess"
  | "sugar-taa"
  | "sugar-path-tracer";

/**
 * Üç üründe de aynı olan post-process seti.
 * `core/viewport/registerPostProcessElements` ile aynı listeyi anlatır.
 */
export const POST_PROCESS_CAPABILITIES: readonly ViewportCapability[] = [
  "ssgi-postprocess",
  "gtao-accumulate-postprocess",
  "outline-postprocess",
  "sugar-taa",
  "sugar-path-tracer",
];

/** Ürün görüntüleyici (model viewer / normod) tipik seti. */
export const PRODUCT_VIEWER_CAPABILITIES: readonly ViewportCapability[] = [
  "smooth-orbit",
  "product-viewer",
  ...POST_PROCESS_CAPABILITIES,
];

/** Sürükle-bırak ile kurgulanan sahne (configurator / room designer) seti. */
export const SCENE_EDITOR_CAPABILITIES: readonly ViewportCapability[] = [
  "pointer-gesture-interpreter",
  "sugar-item-drop",
  "designer-transformer",
  "configurator-bridge",
  ...POST_PROCESS_CAPABILITIES,
];

/** Yeteneğe göre gerekli ek attribute'lar (ör. `product-viewer .data`). */
export type CapabilityProps = Partial<
  Record<ViewportCapability, Record<string, unknown>>
>;

function renderCapability(
  tag: ViewportCapability,
  props: Record<string, unknown> | undefined,
): TemplateResult {
  // Lit statik şablon gerektirdiği için etiketler açıkça yazılıyor;
  // liste tek yerde durduğu sürece amaç korunuyor.
  switch (tag) {
    case "smooth-orbit":
      return html`<smooth-orbit></smooth-orbit>`;
    case "pointer-gesture-interpreter":
      return html`<pointer-gesture-interpreter></pointer-gesture-interpreter>`;
    case "sugar-file-drop":
      return html`<sugar-file-drop
        accept=${(props?.accept as string) ?? ""}
        ?multiple=${!!props?.multiple}
      ></sugar-file-drop>`;
    case "sugar-item-drop":
      return html`<sugar-item-drop
        accept=${(props?.accept as string) ?? ""}
      ></sugar-item-drop>`;
    case "product-viewer":
      return html`<product-viewer .data=${props?.data as never}></product-viewer>`;
    case "designer-transformer":
      return html`<designer-transformer></designer-transformer>`;
    case "configurator-bridge":
      return html`<configurator-bridge></configurator-bridge>`;
    case "configurator-drag-preview":
      return html`<configurator-drag-preview></configurator-drag-preview>`;
    case "ssgi-postprocess":
      return html`<ssgi-postprocess></ssgi-postprocess>`;
    case "gtao-accumulate-postprocess":
      return html`<gtao-accumulate-postprocess></gtao-accumulate-postprocess>`;
    case "outline-postprocess":
      return html`<outline-postprocess></outline-postprocess>`;
    case "sugar-taa":
      return html`<sugar-taa></sugar-taa>`;
    case "sugar-path-tracer":
      return html`<sugar-path-tracer></sugar-path-tracer>`;
    default:
      return html`${nothing}`;
  }
}

export function renderCapabilities(
  capabilities: readonly ViewportCapability[],
  props?: CapabilityProps,
): TemplateResult[] {
  return capabilities.map((tag) => renderCapability(tag, props?.[tag]));
}
